// New goal / edit goal. The form sets everything the Goals list shows: status with its date, and
// the rating.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronLeft, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { ErrorState, Field, FormRow } from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';
import { dayOf, todayIso, useUnsavedGuard } from './clientShared';
import { goalPayload, goalStatus } from './ClientGoals';

const RATINGS = [
  { value: 1, label: 'No progress' },
  { value: 2, label: 'Some progress' },
  { value: 3, label: 'Steady progress' },
  { value: 4, label: 'Good progress' },
  { value: 5, label: 'Goal met' },
];
const STATUSES = [
  { value: 'active', label: 'Active', hint: 'Still being worked on' },
  { value: 'achieved', label: 'Achieved', hint: 'The participant reached it' },
  { value: 'closed', label: 'Closed', hint: 'Stopped without being achieved' },
];

const blank = () => ({ name: '', description: '', commencedAt: todayIso(), status: 'active', outcomeDate: todayIso(), rating: null });

export default function ClientGoalForm() {
  const { id, goalId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const record = useClientRecord(id);
  const isEdit = !!goalId;

  const [form, setForm] = useState(blank);
  const [initial, setInitial] = useState(blank);
  const [original, setOriginal] = useState(null);
  const [state, setState] = useState({ loading: isEdit, error: null });
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState(null);

  const load = useCallback(() => {
    if (!isEdit) return;
    setState({ loading: true, error: null });
    api.get(`/clients/${id}/goals`)
      .then((r) => {
        const g = (Array.isArray(r.data) ? r.data : []).find((x) => x.id === goalId);
        if (!g) { setState({ loading: false, error: 'This goal was not found. It may have been deleted.' }); return; }
        const s = goalStatus(g);
        const f = {
          name: g.name || '', description: g.description || '', commencedAt: dayOf(g.commenced_at),
          status: s, outcomeDate: dayOf(s === 'achieved' ? g.achieved_at : s === 'closed' ? g.closed_at : '') || todayIso(),
          rating: g.rating == null ? null : Number(g.rating),
        };
        setOriginal(g); setForm(f); setInitial(f);
        setState({ loading: false, error: null });
      })
      .catch((e) => setState({ loading: false, error: e.response?.data?.message || 'We couldn’t load this goal. Check your connection, then try again.' }));
  }, [id, goalId, isEdit]);
  useEffect(() => { load(); }, [load]);

  const dirty = !state.loading && JSON.stringify(form) !== JSON.stringify(initial);
  useUnsavedGuard(dirty && !saving, { confirm, navigate, message: 'Your changes to this goal have not been saved.' });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setNameError('Enter the goal'); document.getElementById('goal-name')?.focus(); return; }
    // The status decides which outcome date is set. A legacy goal with both dates keeps both
    // unless its status is changed.
    const keepBoth = original && original.achieved_at && original.closed_at && form.status === 'achieved';
    const achievedAt = form.status === 'achieved' ? form.outcomeDate || todayIso() : null;
    const closedAt = form.status === 'closed' ? form.outcomeDate || todayIso() : (keepBoth ? dayOf(original.closed_at) : null);
    const body = {
      name: form.name.trim(), description: form.description, commencedAt: form.commencedAt || null,
      achievedAt, closedAt, rating: form.rating,
      // Older servers read these flags on create.
      achieved: form.status === 'achieved', closed: form.status === 'closed',
    };
    setSaving(true);
    try {
      if (isEdit) await api.put(`/clients/${id}/goals/${goalId}`, goalPayload(original, body));
      else await api.post(`/clients/${id}/goals`, body);
      setInitial(form);
      toast.success(isEdit ? 'Goal updated' : 'Goal created');
      navigate(`/clients/${id}/goals`);
    } catch (err) { toast.error(err.response?.data?.message || 'Could not save the goal'); }
    finally { setSaving(false); }
  };

  const ratingLabel = RATINGS.find((r) => r.value === form.rating)?.label;

  return (
    <ClientFrame record={record}>
      <div>
        <Link to={`/clients/${id}/goals`} className="inline-flex items-center gap-1 text-[13px] font-medium text-slate-500 hover:text-slate-800">
          <ChevronLeft size={15} aria-hidden /> Goals
        </Link>
        <h2 className="mt-1 text-base font-semibold text-slate-900">{isEdit ? 'Edit goal' : 'New goal'}</h2>
      </div>

      {state.error ? (
        <div className="bg-white border border-slate-200 rounded-xl"><ErrorState message={state.error} onRetry={load} /></div>
      ) : state.loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3 max-w-3xl" aria-busy="true">
          <div className="skeleton h-10" /><div className="skeleton h-10 w-1/2" /><div className="skeleton h-28" />
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="bg-white border border-slate-200 rounded-xl max-w-3xl">
          <div className="p-5 space-y-5">
            <Field htmlFor="goal-name" label="Goal" required error={nameError}>
              <input id="goal-name" className="input-field" value={form.name} aria-invalid={nameError ? true : undefined}
                onChange={(e) => { set('name', e.target.value); setNameError(null); }} placeholder="For example: Catch the bus to work on my own" />
            </Field>
            <Field label="Description">
              {(fid) => <textarea id={fid} className="input-field resize-y" rows={5} value={form.description} onChange={(e) => set('description', e.target.value)} />}
            </Field>
            <FormRow cols={2}>
              <Field label="Started">{(fid) => <input id={fid} type="date" className="input-field" value={form.commencedAt} onChange={(e) => set('commencedAt', e.target.value)} />}</Field>
            </FormRow>

            <fieldset>
              <legend className="text-[13px] font-medium text-slate-700 mb-1.5">Status</legend>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Status">
                {STATUSES.map((s) => {
                  const on = form.status === s.value;
                  return (
                    <label key={s.value} className={clsx('flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors duration-150',
                      on ? 'border-primary-400 bg-primary-50/60' : 'border-slate-200 hover:border-slate-300')}>
                      <input type="radio" name="goal-status" value={s.value} checked={on} onChange={() => set('status', s.value)} className="mt-0.5 accent-primary-600" />
                      <span>
                        <span className="block text-sm font-medium text-slate-800">{s.label}</span>
                        <span className="block text-[12.5px] text-slate-500">{s.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {form.status !== 'active' && (
                <div className="mt-3 max-w-xs">
                  <Field label={form.status === 'achieved' ? 'Date achieved' : 'Date closed'}>
                    {(fid) => <input id={fid} type="date" className="input-field" value={form.outcomeDate} max={todayIso()} onChange={(e) => set('outcomeDate', e.target.value)} />}
                  </Field>
                </div>
              )}
            </fieldset>

            <fieldset>
              <legend className="text-[13px] font-medium text-slate-700 mb-1.5">Rating</legend>
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-lg bg-slate-100 p-0.5" role="radiogroup" aria-label="Rating out of 5">
                  {RATINGS.map((r) => {
                    const on = form.rating === r.value;
                    return (
                      <button key={r.value} type="button" role="radio" aria-checked={on} aria-label={`${r.value} of 5: ${r.label}`} title={r.label}
                        onClick={() => set('rating', r.value)}
                        className={clsx('h-9 w-11 rounded-md text-sm font-semibold tabular-nums transition-colors duration-150',
                          on ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(16,24,40,.08)]' : 'text-slate-600 hover:text-slate-900')}>
                        {r.value}
                      </button>
                    );
                  })}
                </div>
                <span className="text-[13px] text-slate-600">{form.rating ? `${form.rating} / 5 · ${ratingLabel}` : 'Not rated'}</span>
                {form.rating != null && <button type="button" onClick={() => set('rating', null)} className="btn-ghost btn-sm">Clear rating</button>}
              </div>
            </fieldset>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-5 py-3 rounded-b-xl bg-slate-50/60">
            {dirty && <span className="mr-auto flex items-center gap-2 text-[13px] text-slate-700"><span className="w-2 h-2 rounded-full bg-amber-500" aria-hidden />Unsaved changes</span>}
            <Link to={`/clients/${id}/goals`} className="btn-secondary">Cancel</Link>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving && <Loader2 size={15} className="animate-spin" aria-hidden />}
              {saving ? 'Saving…' : isEdit ? 'Save goal' : 'Create goal'}
            </button>
          </div>
        </form>
      )}
    </ClientFrame>
  );
}
